/**
 * landingTheme.js — ตั้งค่าหน้าตาเว็บลูกค้าที่แอดมินปรับได้ (admin-system → หวย → ปรับแต่งหน้าเว็บ)
 *
 * - ดึง config จาก GET /lotto/landing-config แล้วแปลงเป็น CSS variable / data-attribute บน <html>
 *   (styles.css อ่าน --lxc-* , data-lx-theme, data-lx-radius, data-lx-shadow)
 * - โหมดมืด/สว่าง: แอดมินกำหนดค่าเริ่มต้น (light | dark | auto ตามอุปกรณ์) และเปิดให้ลูกค้าสลับเองได้
 *   ค่าที่ลูกค้าเลือกจำไว้ใน localStorage (lx_theme_pref)
 * - cache config ล่าสุดไว้ใน localStorage แล้ว apply ทันทีตอนโหลด → ไม่มีจอวาบสีเดิมก่อนสีใหม่
 * - ถ้า API ล่ม/ยังไม่รัน migration → ใช้ FALLBACK_CONFIG (= หน้าตาเดิมของเว็บ) หน้าเว็บต้องไม่พัง
 *
 * หมายเหตุ: FALLBACK_CONFIG ต้องตรงกับ defaults() ใน server/services/landingConfig.js
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import lottoApi from "./api";

const CFG_KEY = "lx_landing_cfg_v1";
const PREF_KEY = "lx_theme_pref";
const BOOT_BG_KEY = "lx_boot_bg";
// หน้าเมนูเดิม (App.js) ไม่ใช้ธีมนี้ — กันไม่ให้ไปทาสี/ตั้ง attribute ใส่หน้านั้น
const LEGACY_PATH = /^\/(menu|login|legacy)(\/|$)/;

// ซ่อนหน้าผลหวย /results ชั่วคราว (เมนู + ลิงก์ + เข้าตรงจะเด้งกลับหน้าแรก) — เปลี่ยนเป็น true เพื่อเปิดกลับ
export const RESULTS_PAGE_ENABLED = false;

export const NAV_DEFAULTS = {
  home: "หน้าแรก",
  results: "ผลหวย",
  refer: "แนะนำเพื่อน",
  reviews: "รีวิวจากลูกค้า",
  mybets: "โพยของฉัน",
  account: "บัญชี",
};

export const CATEGORY_DEFAULTS = {
  GOV: "หวยรัฐบาล / สถาบันการเงิน",
  FOREIGN: "หวยต่างประเทศ",
  STOCK: "หวยหุ้น",
  YEEKEE: "ยี่กี",
  "4D": "4D",
};

const emptyPalette = () => ({ bg: "", header: "", text: "", accent: "", accent2: "" });

export const FALLBACK_CONFIG = {
  mode: "light",
  allowVisitorToggle: true,
  palette: { light: emptyPalette(), dark: emptyPalette() },
  presetId: "",
  style: { radius: "normal", shadow: "soft", font: "noto" },
  nav: Object.fromEntries(Object.entries(NAV_DEFAULTS).map(([k, label]) => [k, { visible: true, label }])),
  banner: { visible: true },
  hero: {
    visible: true,
    eyebrow: "เว็บหวยออนไลน์ อันดับ 1",
    eyebrowIcon: "solar:crown-star-bold",
    title: "แทงหวยออนไลน์",
    titleHighlight: "ครบทุกสำนัก",
    subtitle: "รัฐบาล · ลาว · ฮานอย · หุ้น · ยี่กีทุก 15 นาที — จ่ายไว โปรแรง ออกผลตรงเวลา",
    badges: [
      { icon: "solar:bolt-bold", label: "ยี่กีทุก 15 นาที" },
      { icon: "solar:wallet-money-bold-duotone", label: "ฝาก-ถอน อัตโนมัติ" },
      { icon: "solar:shield-check-bold", label: "ปลอดภัย 100%" },
    ],
    showAuthCard: true,
    bgImage: "",
  },
  featured: { visible: true, typeCode: "" },
  latest: { visible: true, title: "ผลรางวัลล่าสุดทุกชนิด" },
  categories: Object.entries(CATEGORY_DEFAULTS).map(([key, label]) => ({ key, visible: true, label })),
  fab: { visible: true, side: "right" },
};

/* ---------------------------- สี ---------------------------- */

const HEX = /^#[0-9a-f]{6}$/i;
const cleanHex = (v) => (typeof v === "string" && HEX.test(v.trim()) ? v.trim().toLowerCase() : "");

function luminance(hex) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** สีตัวอักษรที่อ่านชัดที่สุดบนพื้นสีนี้ (ขาว/เกือบดำ) — ใช้กับปุ่ม accent ที่แอดมินเลือกสีเอง */
export function readableOn(hex) {
  const L = luminance(hex);
  const vsWhite = 1.05 / (L + 0.05);
  const vsDark = (L + 0.05) / (luminance("#111111") + 0.05);
  return vsWhite >= vsDark ? "#ffffff" : "#111111";
}

/* ---------------------------- font ---------------------------- */

const FONTS = {
  noto: { family: "Noto Sans Thai", gf: null }, // โหลดอยู่แล้วใน styles.css
  prompt: { family: "Prompt", gf: "Prompt:wght@400;500;600;700;800" },
  kanit: { family: "Kanit", gf: "Kanit:wght@400;500;600;700;800" },
  sarabun: { family: "Sarabun", gf: "Sarabun:wght@400;500;600;700;800" },
  plex: { family: "IBM Plex Sans Thai", gf: "IBM+Plex+Sans+Thai:wght@400;500;600;700" },
};
const FALLBACK_STACK = `system-ui, -apple-system, "Segoe UI", sans-serif`;

function applyFont(root, key) {
  const f = FONTS[key] || FONTS.noto;
  const link = document.getElementById("lx-font-link");
  if (!f.gf) {
    root.style.removeProperty("--lx-font");
    root.style.removeProperty("--lx-display");
    if (link) link.remove();
    return;
  }
  const href = `https://fonts.googleapis.com/css2?family=${f.gf}&display=swap`;
  if (!link) {
    const el = document.createElement("link");
    el.id = "lx-font-link";
    el.rel = "stylesheet";
    el.href = href;
    document.head.appendChild(el);
  } else if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href);
  }
  root.style.setProperty("--lx-font", `"${f.family}", "Noto Sans Thai", ${FALLBACK_STACK}`);
  root.style.setProperty("--lx-display", `"${f.family}", "Noto Sans Thai", ${FALLBACK_STACK}`);
}

/* ---------------------------- รวมค่า ---------------------------- */

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

function mergeDeep(base, patch) {
  if (!isObj(base) || !isObj(patch)) return patch === undefined ? base : patch;
  const out = { ...base };
  Object.keys(patch).forEach((k) => {
    out[k] = isObj(base[k]) && isObj(patch[k]) ? mergeDeep(base[k], patch[k]) : patch[k];
  });
  return out;
}

/** รวมกับค่าเริ่มต้น + กรองสีซ้ำอีกชั้น (server กรองแล้ว แต่ cache ใน localStorage แก้มือได้) */
export function normalizeConfig(raw) {
  const cfg = mergeDeep(FALLBACK_CONFIG, isObj(raw) ? raw : {});
  ["light", "dark"].forEach((m) => {
    const p = isObj(cfg.palette?.[m]) ? cfg.palette[m] : {};
    cfg.palette = { ...cfg.palette, [m]: Object.fromEntries(Object.keys(emptyPalette()).map((k) => [k, cleanHex(p[k])])) };
  });
  if (!["light", "dark", "auto"].includes(cfg.mode)) cfg.mode = "light";
  return cfg;
}

/* ---------------------------- localStorage ---------------------------- */

const store = {
  get(k) {
    try {
      return window.localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k, v) {
    try {
      window.localStorage.setItem(k, v);
    } catch {
      /* private mode / เต็ม — ข้ามได้ */
    }
  },
  del(k) {
    try {
      window.localStorage.removeItem(k);
    } catch {
      /* noop */
    }
  },
};

function readCachedConfig() {
  try {
    const raw = store.get(CFG_KEY);
    return raw ? normalizeConfig(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

const readPref = () => {
  const v = store.get(PREF_KEY);
  return v === "light" || v === "dark" ? v : null;
};

const systemPrefersDark = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

/** โหมดที่ใช้จริง: ลูกค้าเลือกเอง (ถ้าแอดมินเปิดให้สลับ) > ค่าเริ่มต้นของแอดมิน > ตามอุปกรณ์ */
export function resolveMode(cfg, pref, sysDark) {
  if (cfg.allowVisitorToggle && pref) return pref;
  if (cfg.mode === "auto") return sysDark ? "dark" : "light";
  return cfg.mode;
}

/* ---------------------------- apply ลง DOM ---------------------------- */

const PALETTE_VARS = {
  bg: "--lxc-bg",
  header: "--lxc-header",
  text: "--lxc-text",
  accent: "--lxc-accent",
  accent2: "--lxc-accent-2",
};
const DEFAULT_THEME_COLOR = { dark: "#17142b" };

export function applyTheme(cfg, mode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-lx-theme", mode);
  root.setAttribute("data-lx-radius", cfg.style.radius);
  root.setAttribute("data-lx-shadow", cfg.style.shadow);

  const pal = cfg.palette[mode] || emptyPalette();
  Object.entries(PALETTE_VARS).forEach(([k, cssVar]) => {
    if (pal[k]) root.style.setProperty(cssVar, pal[k]);
    else root.style.removeProperty(cssVar);
  });
  // สีตัวอักษรบนปุ่ม/ป้าย — คำนวณจากสีที่แอดมินเลือก (ไม่ตั้ง = css ใช้ค่าเดิม)
  const setOn = (cssVar, bg) => {
    if (bg) root.style.setProperty(cssVar, readableOn(bg));
    else root.style.removeProperty(cssVar);
  };
  setOn("--lxc-on-accent", pal.accent);
  setOn("--lxc-on-accent-2", pal.accent2);
  if (pal.header && !pal.text) root.style.setProperty("--lxc-header-text", readableOn(pal.header));
  else root.style.removeProperty("--lxc-header-text");

  applyFont(root, cfg.style.font);

  // แถบ address bar มือถือให้กลมกลืนกับแถบบนสุด
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    if (!meta.dataset.lxOrig) meta.dataset.lxOrig = meta.getAttribute("content") || "";
    const c = pal.header || pal.bg || DEFAULT_THEME_COLOR[mode] || meta.dataset.lxOrig;
    meta.setAttribute("content", c);
  }
}

/** จำสีพื้นหลังจริงไว้ใช้เป็นสีหน้าจอตอนโหลดครั้งถัดไป (ดู inline script ใน public/index.html) */
function rememberBootBg() {
  try {
    const bg = getComputedStyle(document.documentElement).getPropertyValue("--lx-bg").trim();
    // index.html ใช้เฉพาะ #hex — ค่าอื่น (rgb()/ชื่อสี) ไม่เก็บ จะได้ไม่เหลือค่าเก่าค้าง
    if (/^#[0-9a-f]{3,8}$/i.test(bg)) store.set(BOOT_BG_KEY, bg);
    else store.del(BOOT_BG_KEY);
    const boot = document.getElementById("lx-boot");
    if (boot) boot.remove();
  } catch {
    /* noop */
  }
}

// apply จาก cache ทันทีตั้งแต่ import (ก่อน React render ครั้งแรก) — ยกเว้นหน้าเมนูเดิม
if (typeof window !== "undefined" && !LEGACY_PATH.test(window.location.pathname)) {
  const cached = readCachedConfig();
  if (cached) applyTheme(cached, resolveMode(cached, readPref(), systemPrefersDark()));
}

/* ---------------------------- React context ---------------------------- */

const LandingContext = createContext({
  cfg: FALLBACK_CONFIG,
  mode: "light",
  canToggle: false,
  toggleMode: () => {},
});

export function LandingProvider({ children }) {
  const [cfg, setCfg] = useState(() => readCachedConfig() || FALLBACK_CONFIG);
  const [pref, setPref] = useState(readPref);
  const [sysDark, setSysDark] = useState(systemPrefersDark);

  useEffect(() => {
    let alive = true;
    lottoApi
      .landingConfig()
      .then((raw) => {
        if (!alive || !raw) return;
        const next = normalizeConfig(raw);
        store.set(CFG_KEY, JSON.stringify(next));
        setCfg(next);
      })
      .catch(() => {
        /* ใช้ cache / ค่าเดิมต่อไป */
      });
    return () => {
      alive = false;
    };
  }, []);

  // โหมด "ตามอุปกรณ์" ต้องตามตอนผู้ใช้สลับธีมระบบด้วย
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const on = (e) => setSysDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", on);
    else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else mq.removeListener(on);
    };
  }, []);

  const mode = resolveMode(cfg, pref, sysDark);

  useEffect(() => {
    applyTheme(cfg, mode);
    rememberBootBg();
  }, [cfg, mode]);

  const toggleMode = useCallback(() => {
    const next = mode === "dark" ? "light" : "dark";
    setPref(next);
    store.set(PREF_KEY, next);
  }, [mode]);

  const value = useMemo(
    () => ({ cfg, mode, canToggle: !!cfg.allowVisitorToggle, toggleMode }),
    [cfg, mode, toggleMode],
  );
  return <LandingContext.Provider value={value}>{children}</LandingContext.Provider>;
}

export const useLanding = () => useContext(LandingContext);
